/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Loading } from 'carbon-components-react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { useServiceDeskForm } from '../../../hooks/useServiceDeskForm';
import { ZENDESK_SERVICEDESK_TYPE } from '../../../util/channelTypes';
import { QuickSetupProvider } from '../../common/quick-setup/QuickSetupProvider';
import { Step } from '../../common/quick-setup/Step';
import { Zendesk as messages } from '../messages';
import { ServiceDeskProps } from '../types';
import { WizardButtons } from '../wizard/WizardButtons';
import { Connect } from './Connect';
import { PrivateApp } from './PrivateApp';
import { settingsSchema } from './schema';

export const Zendesk = ({
  assistantId,
  environmentId,
  serviceDeskId,
  updateServiceDeskId,
  readOnly = true,
  webChatFormMethods
}: ServiceDeskProps) => {
  const { formatMessage: t } = useIntl();

  const { formMethods, formLoading } = useServiceDeskForm(
    ZENDESK_SERVICEDESK_TYPE,
    assistantId,
    environmentId,
    serviceDeskId,
    settingsSchema
  );

  const [backClicked, setBackClicked] = useState<number | null>(null);
  const [nextClicked, setNextClicked] = useState<number | null>(null);
  const [enableNext, setEnableNext] = useState<boolean>(false);
  const [enableBack, setEnableBack] = useState<boolean>(false);
  const [showNext, setShowNext] = useState<boolean>(false);
  const [showBack, setShowBack] = useState<boolean>(false);

  const buttonHooks = {
    nextClicked,
    backClicked,
    setEnableNext,
    setEnableBack,
    setShowNext,
    setShowBack
  };

  const handleNext = _event => {
    // pass through to whatever callback is passed.
    setBackClicked(null);
    setNextClicked(Date.now());
  };

  const handleBack = _event => {
    setNextClicked(null);
    setBackClicked(Date.now());
  };

  return (
    <div>
      {formLoading && <Loading />}
      <QuickSetupProvider buttonHooks={{ ...buttonHooks }} readOnly={readOnly}>
        <Step
          className="TabContent__body-outdent"
          label={t(messages.stepOneLabel)}
          secondaryLabel={t(messages.stepOneSecondaryLabel)}
          required={true}>
          <Connect
            assistantId={assistantId}
            environmentId={environmentId}
            formMethods={formMethods}
            updateServiceDeskId={updateServiceDeskId}
          />
        </Step>
        <Step
          className="TabContent__body-outdent"
          label={t(messages.stepTwoLabel)}
          secondaryLabel={t(messages.stepTwoSecondaryLabel)}>
          <PrivateApp
            formMethods={formMethods}
            webChatFormMethods={webChatFormMethods}
          />
        </Step>
      </QuickSetupProvider>
      <WizardButtons
        enableBack={enableBack}
        enableNext={enableNext}
        handleBack={handleBack}
        handleNext={handleNext}
        idType="zd"
        showBack={showBack}
        showNext={showNext}
      />
    </div>
  );
};
